import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AppealsIcon,
  CardsIcon,
  CompaniesIcon,
  ConfigIcon,
  DashboardIcon,
  HealthIcon,
  InspectorsIcon,
  MoneyHealthIcon,
  OrdersIcon,
  PayoutsIcon,
  PickersIcon,
  RefundsIcon,
  ReportsIcon,
  UsersIcon,
  VehicleCatalogIcon,
} from './icons';
import { useQueueCounts } from './QueueCountsProvider';
import {
  SidebarBrandMark,
  SidebarBrandName,
  SidebarFooter,
  SidebarFooterAvatar,
  SidebarGlyph,
  SidebarRoot,
  SidebarSectionHeader,
  SidebarTop,
} from './Sidebar.styles';
import { SidebarItem } from './SidebarItem';
import { SidebarMarketplace } from './SidebarMarketplace';
import { SidebarVitrine } from './SidebarVitrine';
import logoMarkOnDark from '../../assets/logo-mark-on-dark.png';
import { RequirePermission } from '../../auth';
import { Routes } from '../../routes';

/**
 * Sidebar head and foot. AUT-511 (D-3, D-11): both carry the green i-with-lens
 * glyph in place of the dead CarCheck «CC»; the glyph is decorative — the
 * product name sits right next to it as text.
 */
const SidebarBrandTop = (): ReactElement => {
  const { t } = useTranslation();
  return (
    <SidebarTop>
      <SidebarBrandMark>
        <SidebarGlyph src={logoMarkOnDark} alt="" aria-hidden data-testid="sidebar-brand-glyph" />
      </SidebarBrandMark>
      <SidebarBrandName>
        {t('app.title')}
        <small>Admin · BY</small>
      </SidebarBrandName>
    </SidebarTop>
  );
};

const SidebarStatusFooter = (): ReactElement => {
  const { t } = useTranslation();
  return (
    <SidebarFooter>
      <SidebarFooterAvatar>
        <SidebarGlyph src={logoMarkOnDark} alt="" aria-hidden data-testid="sidebar-footer-glyph" />
      </SidebarFooterAvatar>
      <div>
        <div style={{ color: 'rgba(255, 255, 255, 0.85)', fontWeight: 600, fontSize: 12 }}>
          {t('header.statusOnline')}
        </div>
      </div>
    </SidebarFooter>
  );
};

/**
 * Sidebar — pixel-match to `design/mockups/admin/layout.html` (LOCK 11).
 *
 * Sections (mockup order):
 *   - Обзор → Dashboard, Нужно внимание (AUT-328), Состояние (admin-only, /health smoke)
 *   - Площадка (AUT-542, `design/mockups/sm-admin.html` — right under Обзор) →
 *     Специалисты, Объявления (`verify_inspectors`), Жалобы (count of open
 *     complaints), Заявки (`moderate_reports`) — see `SidebarMarketplace`
 *   - Модерация → Заказы (AUT-123), Отчёты (AUT-127), Аппеляции (AUT-130),
 *     Справочник авто (AUT-437 — only with `moderate_reports`, NO count: the
 *     suggestion queue is switched off)
 *   - Финансы → Выплаты (AUT-128), Возвраты (AUT-129), Карты (AUT-126)
 *   - Пользователи → Все пользователи (AUT-124), Инспекторы (AUT-125)
 *   - Инструмент подборщика → Подборщики (AUT-400), Компании (AUT-403). NO
 *     badges (the mockup's «57» / «6» are not queues and `QueueCountsProvider`
 *     counts neither). The mockup's «Заказчики» label is NOT applied: AUT-284
 *     renamed that row to «Все пользователи» on purpose (it lists everyone).
 *   - Настройки → Конфигурация (AUT-131)
 *
 * Live nav targets: Dashboard, Health, Заказы (/orders),
 * Все пользователи (/users), Инспекторы (/inspectors), Отчёты (/reports),
 * Аппеляции (/appeals), Выплаты (/payouts), Возвраты (/refunds),
 * Карты (/cards), Подборщики (/pickers), Компании (/companies),
 * Конфигурация (/config) — every
 * section row is now live.
 */
export const Sidebar = (): ReactElement => {
  const { t } = useTranslation();
  const counts = useQueueCounts();

  return (
    <SidebarRoot>
      <SidebarBrandTop />

      <SidebarSectionHeader>{t('nav.sections.overview')}</SidebarSectionHeader>
      <SidebarItem to={Routes.App.Dashboard} icon={<DashboardIcon />} label={t('nav.dashboard')} />
      {/*
        AUT-328 — «Нужно внимание». NO badge, deliberately, against the mockup:
        `AdminQueueCountsDto.failedPayouts` is a ≤15-minute-stale Redis rollup
        of ONE of the four signals, and putting it next to a tab whose whole
        premise is a live uncached read would be a lie in two directions at
        once. Sourcing a count from the money-health query instead would mean
        lifting that query into `QueueCountsProvider`, which mounts for every
        admin — including the ones without `process_payouts`, for whom it 403s.
        The row is plain; the counts live on the page.
      */}
      <SidebarItem
        to={Routes.App.MoneyHealth}
        icon={<MoneyHealthIcon />}
        label={t('nav.moneyHealth')}
      />
      <SidebarItem to={Routes.App.Health} icon={<HealthIcon />} label={t('nav.health')} />

      <SidebarMarketplace />

      <SidebarVitrine />

      <SidebarSectionHeader>{t('nav.sections.moderation')}</SidebarSectionHeader>
      <SidebarItem
        to={Routes.App.Orders}
        icon={<OrdersIcon />}
        label={t('nav.orders')}
        badgeCount={counts.orders}
      />
      {/*
        AUT-265 — this badge counts STUCK reports (final, unaccepted,
        finalized >24h ago), not reports awaiting moderation. The number
        is small and rare by design; the title says what it means so a
        non-zero badge is read rather than dismissed.
      */}
      <SidebarItem
        to={Routes.App.Reports}
        icon={<ReportsIcon />}
        label={t('nav.reports')}
        badgeCount={counts.reports}
        badgeTitle={t('nav.badges.stuckReports')}
      />
      <SidebarItem
        to={Routes.App.Appeals}
        icon={<AppealsIcon />}
        label={t('nav.appeals')}
        badgeCount={counts.appeals}
      />
      <RequirePermission perm="moderate_reports" fallback={null}>
        <SidebarItem
          to={Routes.App.VehicleCatalog}
          icon={<VehicleCatalogIcon />}
          label={t('nav.vehicleCatalog')}
        />
      </RequirePermission>

      <SidebarSectionHeader>{t('nav.sections.finance')}</SidebarSectionHeader>
      <SidebarItem to={Routes.App.Payouts} icon={<PayoutsIcon />} label={t('nav.payouts')} />
      <SidebarItem to={Routes.App.Refunds} icon={<RefundsIcon />} label={t('nav.refunds')} />
      <SidebarItem to={Routes.App.Cards} icon={<CardsIcon />} label={t('nav.cards')} />

      <SidebarSectionHeader>{t('nav.sections.users')}</SidebarSectionHeader>
      <SidebarItem to={Routes.App.Users} icon={<UsersIcon />} label={t('nav.users')} />
      <SidebarItem
        to={Routes.App.Inspectors}
        icon={<InspectorsIcon />}
        label={t('nav.inspectors')}
        badgeCount={counts.inspectors}
      />

      <SidebarSectionHeader>{t('nav.sections.pickerTool')}</SidebarSectionHeader>
      <SidebarItem to={Routes.App.Pickers} icon={<PickersIcon />} label={t('nav.pickers')} />
      <SidebarItem to={Routes.App.Companies} icon={<CompaniesIcon />} label={t('nav.companies')} />

      <SidebarSectionHeader>{t('nav.sections.settings')}</SidebarSectionHeader>
      <SidebarItem to={Routes.App.Config} icon={<ConfigIcon />} label={t('nav.config')} />

      <SidebarStatusFooter />
    </SidebarRoot>
  );
};
